<template>
  <div>
    <div class="row">
      <div class="col-12 pt-3">
        <SectionTitle has-line content="Resumen de Servicios" />
      </div>
    </div>

    <div class="bold">
      <span class="text-red">Costo mayor al del año anterior</span>
      |
      <span class="text-green">Costo igual al del año anterior</span>
      |
      <span class="text-yellow">Costo menor al del año anterior</span>
    </div>
    <template v-if="services.length > 0">
      <div class="bg-gray-1 p-3 mt-3" v-if="generalServices.length > 0">
        <icon-view
          :has-border="false"
          icon="fas fa-file-alt color-orange"
          icon-size="1rem"
          title="Servicios generales"
          variant="custom-icon-view"
          class="mb-2 icon-view-color-text"
        />
        <template v-for="(item, index) in generalServices">
          <ServiceRow
            :key="index"
            v-model="generalServices[index]"
            :collections="collections"
            :index="index"
            :readonly="readonly"
            :isAcceptBtnDisabled="isAcceptBtnDisabled"
            :academic-year-id="academicYearId"
            :school-id="schoolId"
            @on-service-change="$emit('on-service-change', $event)"
            @remove="removeService"
          />
        </template>
      </div>

      <div class="bg-gray-1 p-3 mt-3" v-if="therapyServices.length > 0">
        <icon-view
          :has-border="false"
          icon="fas fa-file-alt color-orange"
          icon-size="1rem"
          title="Servicios de terapia"
          variant="custom-icon-view"
          class="mb-2 icon-view-color-text"
        />
        <template v-for="(item, index) in therapyServices">
          <ServiceRow
            :key="index"
            v-model="therapyServices[index]"
            :collections="collections"
            :index="index"
            :readonly="readonly"
            :costByServices="costByServices"
            :isAcceptBtnDisabled="isAcceptBtnDisabled"
            :academic-year-id="academicYearId"
            :school-id="schoolId"
            @on-service-change="onServiceChange"
            @remove="removeService"
          />
        </template>
      </div>

      <div class="bg-gray-1 p-3 mt-3" v-if="aditionalServices.length > 0">
        <icon-view
          :has-border="false"
          icon="fas fa-file-alt color-orange"
          icon-size="1rem"
          title="Servicios Adicionales"
          variant="custom-icon-view"
          class="mb-2 icon-view-color-text"
        />
        <template v-for="(item, index) in aditionalServices">
          <ServiceRow
            :key="index"
            v-model="aditionalServices[index]"
            :collections="collections"
            :index="index"
            :costByServices="costByServices"
            :cost-readonly="costReadonly"
            :readonly="readonly"
            :isAcceptBtnDisabled="isAcceptBtnDisabled"
            :academic-year-id="academicYearId"
            :school-id="schoolId"
            @on-service-change="onServiceChange"
            @remove="removeService"
          />
        </template>
      </div>

      <TotalRow
        :hideTotal="hideTotal"
        :items="services"
        :readonly="readonly"
        @add="openAddServicesForm"
      />
    </template>

    <template v-else-if="!showAddServices">
      <ActionButton
        type="activationButton"
        text="Presione aquí para añadir servicios"
        @click="openAddServicesForm"
      />
    </template>

    <template v-if="showAddServices">
      <AddEditServiceForm
        :collections="collections"
        :costByServices="costByServices"
        :cost-readonly="costReadonly"
        :isAcceptBtnDisabled="isAcceptBtnDisabled"
        :statusId="statusId"
        @on-service-change="onServiceChange"
        @cancel="closeAddServicesForm"
        @accept="addService"
      />
    </template>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import ServiceRow from './components/ServiceRow.vue';
import TotalRow from './components/TotalRow.vue';
import AddEditServiceForm from './components/AddEditServiceForm.vue';
import alert from '@/mixin/alert.mixin';
import IconView from 'icon-view';
import centralLevelServicesCategories from '@/utils/constants/tapRequest/centralLevelServicesCategories';

export default {
  name: 'ServicesSummary',
  mixins: [alert],
  components: {
    SectionTitle,
    ServiceRow,
    TotalRow,
    ActionButton,
    AddEditServiceForm,
    IconView,
  },
  props: {
    collections: {
      type: Object,
      default: () => ({
        services: [],
        servicesPreviousYear: [],
        itinerantServices: [],
        frequencies: [],
        modalities: [],
        durations: [],
      }),
    },
    readonly: { type: Boolean, default: false },
    costReadonly: { type: Boolean, default: false },
    hideTotal: { type: Boolean, default: false },
    services: {
      type: Array,
      default: () => [],
    },
    isAcceptBtnDisabled: { type: Boolean, default: false },
    costByServices: { type: Array, default: () => [] },
    statusId: { type: Number, default: 0 },
    academicYearId: { type: Number, default: null },
    schoolId: { type: Number, default: null },
  },
  data() {
    return {
      showAddServices: false,
    };
  },
  computed: {
    hasAnyItems() {
      return this.services.length > 0;
    },
    generalServices() {
      return this.services.filter(
        (c) =>
          c.service.categoryId == centralLevelServicesCategories.GeneralServices
      );
    },
    therapyServices() {
      return this.services.filter(
        (c) =>
          c.service.categoryId == centralLevelServicesCategories.TherapyServices
      );
    },
    aditionalServices() {
      return this.services.filter(
        (c) =>
          c.service.categoryId ==
          centralLevelServicesCategories.AditionalServices
      );
    },
  },
  watch: {
    services: {
      handler() {
        const value = this.hasAnyItems;
        this.$emit('input', value);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    onServiceChange(data) {
      this.$emit('on-service-change', data);
    },
    openAddServicesForm() {
      if (this.readonly) return;

      this.showAddServices = true;
    },
    closeAddServicesForm() {
      this.showAddServices = false;
    },
    addService(service) {
      this.$emit('add-service', service);

      this.closeAddServicesForm();
    },
    async removeService(serviceId) {
      const confirmation = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea eliminar el servicio?',
        'Sí',
        'No'
      );

      if (!confirmation) return;

      this.$emit('remove-service', serviceId);
    },
  },
};
</script>

<style lang="scss" scoped>
.text-yellow {
  color: #ffc107;
}

.text-red {
  color: #c9545e;
}

.icon-view-color-text {
  ::v-deep .color-gray-7 {
    color: #11291b !important;
  }
}
</style>
